import { ReactElement, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { RecoilRoot } from 'recoil';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import {
  getTokenWithRedirectFallback,
  RouterNavigationClient,
  TqAuthProvider,
} from '@tq/auth-components';
import { EventMessage, EventType, PublicClientApplication } from '@azure/msal-browser';
import { GrowthBookProvider } from '@growthbook/growthbook-react';
import { Layout, ThemeProvider } from 'components';
import 'i18n';
import { b2cAuthority, b2cPolicySignIn, b2cScope } from 'tools';
import AzureAppInsights from './components/AppInsights/AzureAppInsights';
import { useGrowthBook } from 'hooks/useGrowthBook';
import { App } from 'App';
import { setPca } from './api';

const queryClient = new QueryClient();

export const Root = ({ pca }: { pca: PublicClientApplication }): ReactElement => {
  const navigate = useNavigate();

  useEffect(() => {
    let callbackId: string | null = null;

    const setupMsal = async () => {
      try {
        await pca.initialize();
        const navigationClient = new RouterNavigationClient(navigate);
        pca.setNavigationClient(navigationClient);

        callbackId = pca.addEventCallback(async (message: EventMessage) => {
          if (message.eventType === EventType.LOGOUT_START) {
            localStorage.setItem('logout', 'true');
          }
          if (message.eventType === EventType.HANDLE_REDIRECT_END) {
            if (pca.getAllAccounts().length === 0) {
              getTokenWithRedirectFallback(pca, b2cScope, {
                authority: b2cAuthority,
                signInPolicyPath: b2cPolicySignIn,
                impersonatePolicyPath: '',
              }).catch(async (err) => {
                console.error('Guard caught getToken error:', err);
                if (!localStorage.getItem('logout')) {
                  await pca.loginRedirect({
                    scopes: [process.env.REACT_APP_B2C_SCOPE || ''],
                  });
                }
                localStorage.removeItem('logout');
              });
            }
          }
        });

        setPca(pca);
      } catch (error) {
        console.error('Guard caught PCA Initialization failure:', error);
      }
    };

    setupMsal();

    return () => {
      if (callbackId) {
        pca.removeEventCallback(callbackId);
      }
    };
  }, [navigate, pca]);

  const growthBook = useGrowthBook();

  return (
    <RecoilRoot>
      <TqAuthProvider
        acquireTokenRequest={{
          scopes: [b2cScope],
          authority: b2cAuthority + '/' + b2cPolicySignIn,
        }}
        instance={pca}
      >
        <QueryClientProvider client={queryClient}>
          <GrowthBookProvider growthbook={growthBook}>
            <ThemeProvider>
              <AzureAppInsights>
                <Layout>
                  <App />
                </Layout>
              </AzureAppInsights>
            </ThemeProvider>
          </GrowthBookProvider>
        </QueryClientProvider>
      </TqAuthProvider>
    </RecoilRoot>
  );
};
